import { useEffect, useState } from 'react';
import { WaterPump } from './WaterPump';

interface WaterPumpAnimationProps {
  /** 펌프 상태: '운전', '정지', '대기' */
  status: '운전' | '정지' | '대기';
  /** 애니메이션 속도 (초 단위) */
  animationSpeed?: number;
  /** 크기 클래스명 */
  size?: string;
  /** 추가 스타일 클래스명 */
  className?: string;
  /** 수동 제어 시 애니메이션 상태 변경 콜백 */
  onAnimationToggle?: (isRunning: boolean) => void;
  /** 물 흐름 효과 표시 여부 */
  showWaterFlow?: boolean;
}

/**
 * 전동 물 펌프 애니메이션 컴포넌트
 * 임펠러 회전과 물 흐름 효과를 포함한 애니메이션
 *
 * @param status - 펌프 상태 ('운전', '정지', '대기')
 * @param animationSpeed - 애니메이션 속도 (기본값: 2초)
 * @param size - 크기 클래스명 (기본값: 'w-16 h-16')
 * @param className - 추가 스타일 클래스명
 * @param manualControl - 수동 제어 모드 활성화
 * @param onAnimationToggle - 수동 제어 시 애니메이션 상태 변경 콜백
 * @param showWaterFlow - 물 흐름 효과 표시 여부
 */
export default function WaterPumpAnimation({
  status,
  size = 'w-52 h-52',
  className = '',
  onAnimationToggle,
}: WaterPumpAnimationProps) {
  const [isAnimating, setIsAnimating] = useState(status === '운전');

  useEffect(() => {
    setIsAnimating(status === '운전');
  }, [status]);

  const getStatusColor = () => {
    switch (status) {
      case '운전':
        return 'text-blue-600'; // 물 펌프는 파란색이 더 적합
      case '정지':
        return 'text-red-600';
      case '대기':
        return 'text-amber-600';
      default:
        return 'text-gray-600';
    }
  };

  const getBackgroundColor = () => {
    switch (status) {
      case '운전':
        return 'bg-blue-100';
      case '정지':
        return 'bg-red-100';
      case '대기':
        return 'bg-amber-100';
      default:
        return 'bg-gray-100';
    }
  };

  const handleToggle = () => {
    const newAnimatingState = !isAnimating;
    setIsAnimating(newAnimatingState);
    onAnimationToggle?.(newAnimatingState);
  };

  const pumpContainerClass = `
    ${size}
    rounded-full
    ${getBackgroundColor()}
    flex
    items-center
    justify-center
    transition-all
    duration-300
    ${className}
    relative
    overflow-hidden
  `
    .trim()
    .replace(/\s+/g, ' ');

  return (
    <div className="flex flex-col items-center gap-2">
      <div className={pumpContainerClass} onClick={handleToggle}>
        <WaterPump
          className="w-full h-full object-contain"
          running={status === '운전'}
        />

        {/* 정지 중: 진입금지 아이콘 오버랩 */}
        {status === '정지' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative bg-red-600 rounded-full w-16 h-16 flex items-center justify-center shadow-xl border-4 border-red-700 animate-pulse">
              {/* 진입금지 가로선 */}
              <div className="absolute bg-white h-2 w-10 rounded-full"></div>
              {/* 외곽 테두리 강조 */}
              <div className="absolute inset-0 rounded-full border-2 border-white opacity-90"></div>
            </div>
          </div>
        )}

        {/* 대기 중: 일시정지 아이콘 오버랩 */}
        {status === '대기' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative bg-amber-600 rounded-full w-16 h-16 flex items-center justify-center shadow-xl border-4 border-amber-700 animate-pulse">
              {/* 일시정지 두 막대 */}
              <div className="flex gap-1.5">
                <div className="bg-white w-2 h-8 rounded-sm"></div>
                <div className="bg-white w-2 h-8 rounded-sm"></div>
              </div>
              {/* 외곽 테두리 강조 */}
              <div className="absolute inset-0 rounded-full border-2 border-white opacity-90"></div>
            </div>
          </div>
        )}
      </div>

      <div className="text-center">
        <p className={`text-lg font-medium ${getStatusColor()}`}>{status}</p>
      </div>
    </div>
  );
}
